import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchCustomers,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    setPagination,
    setSort,
    searchCustomers,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination
} from '../../store/modules/customer/customerActions';
import CustomerForm from '../../components/pageModule/Customers/CustomerForm';
import CloudActionButtons, { isContactCloudLocked } from '../../components/pageModule/Customers/CloudActionButtons';
import { useHasPermission } from '../../utils/permissionUtils';
import customerService from '../../services/customerService';

const MySwal = withReactContent(Swal);

const Customers = ({
    excludedActions = [],
    onSelectedRowsChange = () => { },
    onClickedRow = () => { },
    externalCondition = {},
    selectedIds = [],
    columnKeys,
    columns: columnsOverride,
}) => {

    const hasCreatePermission = useHasPermission(['create customers']);
    const hasUpdatePermission = useHasPermission(['update customers']);
    const hasViewPermission = useHasPermission(['view customers']);

    const customerState = useSelector(state => state.customer);
    const dispatch = useDispatch();
    const [cloudLoadingByCustomer, setCloudLoadingByCustomer] = useState({});

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createCustomer(formData));
            toast.success('Customer created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating customer', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateCustomer(id, formData));
            toast.success('Customer updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating customer', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteCustomer(id));
                toast.success('Customer deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting customer', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const handleCloudPush = useCallback(async (row) => {
        if (!row?.id) {
            return;
        }

        if (isContactCloudLocked(row?.cloud)) {
            toast.warning('Customer cloud sync is locked', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return;
        }

        setCloudLoadingByCustomer(prev => ({ ...prev, [row.id]: true }));
        try {
            await customerService.pushCloud(row.id);
            toast.success('Customer queued for cloud sync', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            await dispatch(fetchCustomers({}, {}, true, externalCondition));
        } catch (error) {
            toast.error(error.message || 'Error pushing customer to cloud', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setCloudLoadingByCustomer(prev => ({ ...prev, [row.id]: false }));
        }
    }, [dispatch, externalCondition]);

    const allColumns = useMemo(() => [
        {
            id: 'customer_code',
            name: 'Customer Code',
            selector: row => row.customer_code,
            wrap: true,
            resizable: true,
            minWidth: '180px',
        },
        {
            id: 'name',
            name: 'Name',
            selector: row => row.name?.en ?? '-',
            sortable: true,
            sortField: 'users.name',
            grow: 2,
            resizable: true,
            minWidth: '160px',
        },
        {
            id: 'phone',
            name: 'Phone',
            selector: row => row.phone,
            resizable: true,
            minWidth: '120px',
        },
        {
            id: 'customer_type',
            name: 'Customer Type',
            selector: row => row.customer_type === '1' ? 'LPO Customer' : 'Walk-in Customer',
            sortField: 'users.customer_type',
            sortable: true,
            wrap: true,
            minWidth: '120px'
        },
        {
            id: 'status',
            name: 'Status',
            selector: row => row.is_active ?
                <span className="badge bg-success">Active</span> :
                <span className="badge bg-danger">Inactive</span>,
            sortField: 'users.is_active',
            sortable: true
        },
        // {
        //     id: 'cloud',
        //     name: 'Cloud',
        //     minWidth: '70px',
        //     cell: (row) => (
        //         <CloudActionButtons
        //             row={row}
        //             onPush={handleCloudPush}
        //             isPushing={!!cloudLoadingByCustomer[row?.id]}
        //         />
        //     ),
        //     ignoreRowClick: true,
        //     allowOverflow: true,
        // },
    ], [handleCloudPush, cloudLoadingByCustomer]);

    const columns = useMemo(() => {
        if (columnsOverride) {
            return columnsOverride;
        }
        if (!columnKeys?.length) {
            return allColumns;
        }
        const keySet = new Set(columnKeys);
        return allColumns.filter(col => keySet.has(col.id));
    }, [allColumns, columnKeys, columnsOverride]);


    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter, externalCondition) => dispatch(fetchCustomers(queryFilter, activeFilter, shouldUseFilter, externalCondition)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        search: (search) => dispatch(searchCustomers(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),
    };

    let defaultAvailableActions = ['search', 'filter'];
    if (hasCreatePermission) {
        defaultAvailableActions = ['create', ...defaultAvailableActions];
    }
    if (hasViewPermission || hasUpdatePermission) {
        defaultAvailableActions = ['actions', 'edit', ...defaultAvailableActions];
    }
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title="Customers"
                moduleName="Customer"
                moduleKey="customers"
                state={customerState}
                actions={actions}
                FormComponent={CustomerForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                selectableRows={false}
                onSelectedRowsChange={onSelectedRowsChange}
                onClickedRow={onClickedRow}
                urlKey="customers"
                actionType="url"
                selectedIds={selectedIds}
                externalCondition={externalCondition}
            />
        </>
    );
};

export default Customers; 